/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Injectable} from '@angular/core';
import {
  FormCategory,
  FormFieldAttribute,
  FormSubCategory,
} from '../models/FormCategory';
import {
  SchemaDTO,
  SchemaPropertyDTO,
  SchemaTranslationEntry,
  SchemaTranslationsMap,
  UISchemaDTO,
  UiTreeLeafNodeDTO,
} from '../models/Schema';

type SchemaTraversalNode = {
  properties?: Record<string, SchemaPropertyDTO>;
  required?: string[];
};

/**
 * Maps an ui:tree structure to FormCategory[] for schema-driven rendering.
 *
 * The ui:tree is the **rendering master** — it declares what is shown, in what order,
 * and with which component type. The JSON Schema is used as a fallback for technical
 * field metadata (type, validation constraints, units) only.
 *
 * Translation labels for attributes are resolved via:
 * 1. Schema-specific translation file (by valuePath)
 * 2. JSON Schema description
 * 3. valuePath itself (last resort)
 *
 * @see BL-5301 (TDR-027) for the full ui:tree design specification.
 */
@Injectable({
  providedIn: 'root',
})
export class UiTreeHandlerService {
  /**
   * Maps an ui:tree to a FormCategory[] structure.
   * Missing field metadata is resolved from the provided JSON Schema.
   * Attribute labels are resolved via the schema-specific translation file (if available).
   * Returns an empty array if ui:tree is absent or empty.
   *
   * @param uiSchema The UISchemaDTO containing the ui:tree
   * @param schema The JSON Schema for fallback metadata resolution
   * @param schemaTranslations Optional flat JSON object with valuePath → label mappings
   * @param requiredPaths Optional slash-delimited paths of required fields (e.g., `/generalProductInformation/name`)
   * @returns Array of FormCategory objects ready for rendering
   */
  static mapUiTreeToFormCategories(
    uiSchema: UISchemaDTO | undefined,
    schema?: SchemaDTO,
    schemaTranslations?: SchemaTranslationsMap,
    requiredPaths?: string[],
  ): FormCategory[] {
    if (this.isEmpty(uiSchema)) {
      return [];
    }

    const uiTree = uiSchema?.data['ui:tree'] ?? [];

    return uiTree
      .map((categoryEntry) => {
        const keys = Object.keys(categoryEntry);
        if (!keys.length) {
          return null;
        }

        const categoryName = keys[0];
        const categoryNode = categoryEntry[categoryName];

        if (!this.isRecord(categoryNode)) {
          return null;
        }

        const category: FormCategory = {
          name: categoryName,
          translation: {
            label: this.resolveUiLabel(
              categoryNode['ui:label'],
              categoryName,
              schemaTranslations,
            ),
          },
          attributes: [],
        };

        Object.entries(categoryNode).forEach(
          ([subCategoryName, subCategoryNode]) => {
            if (subCategoryName === 'ui:label') {
              return;
            }

            // Backward-compatible ui:tree shape: category -> leaf
            if (this.isLeafNode(subCategoryNode)) {
              const schemaProp = this.resolveSchemaProperty(
                schema,
                subCategoryNode.valuePath,
              );
              const attributeLabel = this.resolveAttributeLabel(
                subCategoryNode.valuePath,
                schemaProp,
                schemaTranslations,
              );
              const attribute = this.mergeLeafWithSchema(
                subCategoryNode,
                schemaProp,
                attributeLabel,
                schemaTranslations,
                schema,
                requiredPaths,
              );
              attribute.name = subCategoryName;

              if (!schemaProp) {
                attribute._schemaError = true;
              }

              category.attributes.push(attribute as unknown as FormSubCategory);
              return;
            }

            if (!this.isRecord(subCategoryNode)) {
              return;
            }

            const subCategory: FormSubCategory = {
              name: subCategoryName,
              translation: {
                label: this.resolveUiLabel(
                  subCategoryNode['ui:label'],
                  subCategoryName,
                  schemaTranslations,
                ),
              },
              attributes: [],
            };

            Object.entries(subCategoryNode).forEach(
              ([attributeName, leafNode]) => {
                if (
                  attributeName === 'ui:label' ||
                  !this.isLeafNode(leafNode)
                ) {
                  return;
                }

                const schemaProp = this.resolveSchemaProperty(
                  schema,
                  leafNode.valuePath,
                );
                const attributeLabel = this.resolveAttributeLabel(
                  leafNode.valuePath,
                  schemaProp,
                  schemaTranslations,
                );

                const attribute = this.mergeLeafWithSchema(
                  leafNode,
                  schemaProp,
                  attributeLabel,
                  schemaTranslations,
                  schema,
                  requiredPaths,
                );
                attribute.name = attributeName;

                if (!schemaProp) {
                  attribute._schemaError = true;
                }

                subCategory.attributes.push(attribute);
              },
            );

            category.attributes.push(subCategory);
          },
        );
        return category;
      })
      .filter((category): category is FormCategory => !!category);
  }

  static isEmpty(uiSchema: UISchemaDTO | undefined): boolean {
    return (
      !uiSchema?.data?.['ui:tree'] || uiSchema.data['ui:tree'].length === 0
    );
  }

  private static resolveSchemaProperty(
    schema: SchemaDTO | undefined,
    valuePath: string,
  ): SchemaPropertyDTO | null {
    if (!schema?.data?.schema?.properties || !valuePath) {
      return null;
    }

    const segments = valuePath.split('.');
    let currentProperties = schema.data.schema.properties;
    let currentProperty: SchemaPropertyDTO | undefined;

    for (let index = 0; index < segments.length; index += 1) {
      const segment = segments[index];
      currentProperty = currentProperties?.[segment];

      if (!currentProperty) {
        return null;
      }

      if (index === segments.length - 1) {
        return currentProperty;
      }

      if (currentProperty.properties) {
        currentProperties = currentProperty.properties;
      } else if (currentProperty.items?.properties) {
        currentProperties = currentProperty.items.properties;
      } else {
        return null;
      }
    }

    return null;
  }

  private static resolveAttributeLabel(
    valuePath: string,
    schemaProp: SchemaPropertyDTO | null,
    schemaTranslations?: SchemaTranslationsMap,
  ): string {
    const translationEntry = this.getTranslationEntry(
      valuePath,
      schemaTranslations,
    );
    return translationEntry?.label ?? schemaProp?.description ?? valuePath;
  }

  private static mergeLeafWithSchema(
    leaf: UiTreeLeafNodeDTO,
    schemaProp: SchemaPropertyDTO | null,
    attributeLabel: string,
    schemaTranslations?: SchemaTranslationsMap,
    schema?: SchemaDTO,
    requiredPaths?: string[],
  ): FormFieldAttribute {
    const translationEntry = this.getTranslationEntry(
      leaf.valuePath,
      schemaTranslations,
    );
    const attribute: FormFieldAttribute = {
      name: '',
      valuePath: leaf.valuePath,
      type:
        (leaf.type as FormFieldAttribute['type'] | undefined) ??
        this.mapSchemaTypeToFormFieldType(schemaProp),
      translation: {
        label: attributeLabel,
        info: translationEntry?.info,
      },
      digits: leaf.digits ?? this.getDecimalDigits(schemaProp?.multipleOf),
      min: leaf.min ?? schemaProp?.minimum,
      max: leaf.max ?? schemaProp?.maximum,
      format: leaf.format ?? schemaProp?.format,
      timeZone: leaf.timeZone,
      unit: leaf.unit ?? schemaProp?.unit,
      isDisabled: leaf.isDisabled ?? schemaProp?.readOnly,
      isAttributeSelectionDisabled: leaf.isAttributeSelectionDisabled,
      isRequired: this.resolveIsRequired(leaf, schema, requiredPaths),
      options:
        leaf.options?.map((o) => ({
          label:
            leaf.type === 'select'
              ? this.resolveSelectOptionLabel(
                  o.value,
                  leaf.valuePath,
                  o['ui:label'],
                  schemaTranslations,
                )
              : this.resolveUiLabel(
                  o['ui:label'],
                  o['ui:label'] ?? o.value,
                  schemaTranslations,
                ),
          value: o.value,
        })) ?? schemaProp?.enum?.map((entry) => ({label: entry, value: entry})),
      chartConfig: leaf.chartConfig,
      data: leaf.data,
      visibleIf: leaf.visibleIf,
    };

    if (leaf.attributes?.length) {
      attribute.attributes = leaf.attributes.map((subAttribute) => {
        // Sub-attributes are copied 1:1. Name is derived from the last valuePath segment if absent.
        const derivedName =
          subAttribute.valuePath.split('.').pop() || subAttribute.valuePath;
        // Sub-attribute valuePaths are stored relative to their parent (e.g. list item
        // fields like "labelingSubject" under "labeling.labels"). Translation entries,
        // however, are always keyed by the fully-qualified path
        // (e.g. "labeling.labels.labelingSubject"). Build that full path for lookups.
        const subAttributeFullPath = this.buildFullValuePath(
          leaf.valuePath,
          subAttribute.valuePath,
        );
        const subAttributeTranslationEntry = this.getTranslationEntry(
          subAttributeFullPath,
          schemaTranslations,
        );
        return {
          name: derivedName,
          valuePath: subAttribute.valuePath,
          type:
            (subAttribute.type as FormFieldAttribute['type'] | undefined) ??
            'text',
          translation: {
            label:
              subAttributeTranslationEntry?.label ??
              subAttribute['ui:label'] ??
              derivedName,
            info: subAttributeTranslationEntry?.info,
          },
          digits: subAttribute.digits,
          min: subAttribute.min,
          max: subAttribute.max,
          format: subAttribute.format,
          unit: subAttribute.unit,
          isDisabled: subAttribute.isDisabled,
          isAttributeSelectionDisabled:
            subAttribute.isAttributeSelectionDisabled,
          isRequired: this.resolveIsRequired(
            {...subAttribute, valuePath: subAttributeFullPath},
            schema,
            requiredPaths,
          ),
          options: subAttribute.options?.map((o) => ({
            label:
              subAttribute.type === 'select'
                ? this.resolveSelectOptionLabel(
                    o.value,
                    subAttributeFullPath,
                    o['ui:label'],
                    schemaTranslations,
                  )
                : this.resolveUiLabel(
                    o['ui:label'],
                    o['ui:label'] ?? o.value,
                    schemaTranslations,
                  ),
            value: o.value,
          })),
          data: subAttribute.data,
          attributes: subAttribute.attributes?.map((nested) => {
            const nestedName =
              nested.valuePath.split('.').pop() || nested.valuePath;
            const nestedFullPath = this.buildFullValuePath(
              subAttributeFullPath,
              nested.valuePath,
            );
            const nestedTranslationEntry = this.getTranslationEntry(
              nestedFullPath,
              schemaTranslations,
            );
            return {
              name: nestedName,
              valuePath: nested.valuePath,
              type:
                (nested.type as FormFieldAttribute['type'] | undefined) ??
                'text',
              translation: {
                label:
                  nestedTranslationEntry?.label ??
                  nested['ui:label'] ??
                  nestedName,
                info: nestedTranslationEntry?.info,
              },
              digits: nested.digits,
              min: nested.min,
              max: nested.max,
              format: nested.format,
              unit: nested.unit,
              isDisabled: nested.isDisabled,
              isAttributeSelectionDisabled: nested.isAttributeSelectionDisabled,
              isRequired: this.resolveIsRequired(
                {...nested, valuePath: nestedFullPath},
                schema,
                requiredPaths,
              ),
              options: nested.options?.map((o) => ({
                label:
                  nested.type === 'select'
                    ? this.resolveSelectOptionLabel(
                        o.value,
                        nestedFullPath,
                        o['ui:label'],
                        schemaTranslations,
                      )
                    : this.resolveUiLabel(
                        o['ui:label'],
                        o['ui:label'] ?? o.value,
                        schemaTranslations,
                      ),
                value: o.value,
              })),
              data: nested.data,
            };
          }),
        };
      });
    }

    return attribute;
  }

  private static mapSchemaTypeToFormFieldType(
    schemaProp: SchemaPropertyDTO | null,
  ): FormFieldAttribute['type'] {
    if (!schemaProp) {
      return 'text';
    }

    if (schemaProp.enum?.length) {
      return 'select';
    }

    if (schemaProp.type === 'string') {
      return this.mapStringSchemaFormatToFormFieldType(schemaProp.format);
    }

    return this.mapSchemaPrimitiveTypeToFormFieldType(schemaProp.type);
  }

  private static mapSchemaPrimitiveTypeToFormFieldType(
    schemaType: string,
  ): FormFieldAttribute['type'] {
    const typeMap: Record<string, FormFieldAttribute['type']> = {
      number: 'number',
      integer: 'number',
      boolean: 'boolean',
      array: 'list',
      object: 'detail',
    };

    return typeMap[schemaType] ?? 'text';
  }

  private static mapStringSchemaFormatToFormFieldType(
    format?: string,
  ): FormFieldAttribute['type'] {
    const formatMap: Record<string, FormFieldAttribute['type']> = {
      'date': 'date',
      'date-time': 'date',
      'email': 'email',
      'uri': 'url',
      'url': 'url',
    };

    return formatMap[format ?? ''] ?? 'text';
  }

  private static getDecimalDigits(multipleOf?: number): number | undefined {
    if (multipleOf === undefined || multipleOf === null) {
      return undefined;
    }

    if (!Number.isFinite(multipleOf)) {
      return undefined;
    }

    const asString = multipleOf.toString();
    if (asString.includes('e-')) {
      return Number(asString.split('e-')[1]);
    }

    const decimalPart = asString.split('.')[1];
    return decimalPart ? decimalPart.length : 0;
  }

  private static resolveRequiredFlag(
    leaf: UiTreeLeafNodeDTO,
    schema?: SchemaDTO,
  ): boolean {
    if (leaf.isRequired !== undefined) {
      return leaf.isRequired;
    }

    if (!schema || !leaf.valuePath) {
      return false;
    }

    return this.isValuePathRequired(schema, leaf.valuePath);
  }

  private static resolveIsRequired(
    leaf: UiTreeLeafNodeDTO,
    schema?: SchemaDTO,
    requiredPaths?: string[],
  ): boolean {
    // Explicit flag takes precedence
    if (leaf.isRequired !== undefined) {
      return leaf.isRequired;
    }
    // If requiredPaths provided, check if valuePath is in it
    if (requiredPaths && leaf.valuePath) {
      const normalizedLeafPath = this.normalizeToSlashPath(leaf.valuePath);

      if (requiredPaths.includes(normalizedLeafPath)) {
        return true;
      }

      // Arrays in requiredPaths often include concrete indices (e.g. /labels/0/name).
      // Match those paths against index-free ui-tree valuePaths as a fallback.
      const indexAgnosticLeafPath = this.stripArrayIndexes(normalizedLeafPath);
      const hasIndexAgnosticMatch = requiredPaths.some((requiredPath) => {
        const normalizedRequiredPath = this.normalizeToSlashPath(requiredPath);
        return (
          this.stripArrayIndexes(normalizedRequiredPath) ===
          indexAgnosticLeafPath
        );
      });

      if (hasIndexAgnosticMatch) {
        return true;
      }

      // Ancestor-prefix match: requiredPaths may mark a parent *object* as required
      // (e.g. "/performance/batteryCondition/stateOfCertifiedEnergy") while the leaf
      // points to a child field inside it. Without a schema a required parent implies
      // required children; with a schema each step below the ancestor must also be in
      // the real `required` array of its parent, so optional children stay optional.
      // Guard: always compare with "prefix + /" to avoid partial-segment collisions
      // ("/foo/bar" must NOT match "/foo/barExtra").
      const hasRequiredAncestor = requiredPaths.some((requiredPath) => {
        const normalizedRequiredPath = this.normalizeToSlashPath(requiredPath);
        const strippedRequired = this.stripArrayIndexes(normalizedRequiredPath);
        if (!indexAgnosticLeafPath.startsWith(`${strippedRequired}/`)) {
          return false;
        }
        return this.isRequiredBelowAncestor(
          schema,
          indexAgnosticLeafPath,
          strippedRequired,
        );
      });

      if (hasRequiredAncestor) {
        return true;
      }

      // Descendant match: the leaf may be a composite/group field (e.g. the "address"
      // field "manufacturerAddress" whose valuePath is
      // "generalProductInformation.manufacturerInformation") that never appears itself
      // in requiredPaths because its JSON schema node declares its own nested `required`
      // array — in that case `RequiredFieldPathExtractor` only emits the concrete leaf
      // paths beneath it (e.g. ".../manufacturerInformation/contactName"), never the
      // group path itself. If ANY required path is nested underneath this leaf's path,
      // the group as a whole must be treated as required too.
      // Guard: always compare with "prefix + /" to avoid partial-segment collisions
      // ("/foo/bar" must NOT match "/foo/barExtra/baz").
      return requiredPaths.some((requiredPath) => {
        const normalizedRequiredPath = this.normalizeToSlashPath(requiredPath);
        const strippedRequired = this.stripArrayIndexes(normalizedRequiredPath);
        return strippedRequired.startsWith(`${indexAgnosticLeafPath}/`);
      });
    }

    // Fallback to schema-based resolution
    return this.resolveRequiredFlag(leaf, schema);
  }

  /**
   * Checks that every segment of `leafPath` below `ancestorPath` is declared in the `required`
   * array of its parent schema node (arrays are traversed through `items`).
   * Returns true when no schema is available or a segment cannot be resolved in it,
   * keeping the legacy "required parent implies required children" behaviour.
   */
  private static isRequiredBelowAncestor(
    schema: SchemaDTO | undefined,
    leafPath: string,
    ancestorPath: string,
  ): boolean {
    let currentNode: SchemaTraversalNode | undefined = schema?.data?.schema;
    if (!currentNode) {
      return true;
    }

    const segments = leafPath.split('/').filter(Boolean);
    const ancestorDepth = ancestorPath.split('/').filter(Boolean).length;

    for (let index = 0; index < segments.length; index += 1) {
      const segment = segments[index];
      const property: SchemaPropertyDTO | undefined =
        currentNode?.properties?.[segment];
      if (!property) {
        return true;
      }
      if (
        index >= ancestorDepth &&
        !(currentNode?.required ?? []).includes(segment)
      ) {
        return false;
      }
      currentNode = property.items ?? property;
    }

    return true;
  }

  private static normalizeToSlashPath(path: string): string {
    const trimmedPath = path.trim();
    const withoutLeadingSlash = trimmedPath.replace(/^\/+/, '');

    return `/${withoutLeadingSlash
      .replace(/\[(\d+)\]/g, '/$1')
      .replace(/\./g, '/')
      .replace(/\/+/g, '/')}`;
  }

  private static stripArrayIndexes(path: string): string {
    return path.replace(/\/\d+(?=\/|$)/g, '');
  }

  private static isValuePathRequired(
    schema: SchemaDTO,
    valuePath: string,
  ): boolean {
    const segments = valuePath.split('.').filter(Boolean);
    if (!segments.length) {
      return false;
    }
    if (!schema.data?.schema) {
      return false;
    }

    // Start at root object schema and walk down to the parent object of the target field.
    let currentNode: SchemaTraversalNode | undefined = schema.data.schema;

    for (let index = 0; index < segments.length; index += 1) {
      const segment = segments[index];
      const isLast = index === segments.length - 1;
      if (isLast) {
        return Array.isArray(currentNode?.required)
          ? currentNode.required.includes(segment)
          : false;
      }

      const nextProp: SchemaPropertyDTO | undefined =
        currentNode?.properties?.[segment];
      if (!nextProp) {
        return false;
      }

      currentNode = nextProp.items ?? nextProp;
    }

    return false;
  }

  private static isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private static isLeafNode(value: unknown): value is UiTreeLeafNodeDTO {
    return this.isRecord(value) && typeof value['valuePath'] === 'string';
  }

  private static readUiLabel(value: unknown, fallback: string): string {
    return typeof value === 'string' && value.length > 0 ? value : fallback;
  }

  /**
   * Builds the fully-qualified translation lookup path for a nested (sub-attribute or
   * nested-attribute) ui:tree node.
   *
   * Sub-attribute and nested-attribute `valuePath` values are stored **relative** to their
   * direct parent (e.g. list-item fields like `labelingSubject` under the parent
   * `labeling.labels`), while translation entries are always keyed by the fully-qualified,
   * dot-delimited path (e.g. `labeling.labels.labelingSubject`). This helper reconstructs
   * that fully-qualified path for translation lookups without mutating the `valuePath`
   * used for data binding.
   *
   * @param parentPath The valuePath of the direct parent node
   * @param childPath The relative valuePath of the child node
   * @returns The fully-qualified, dot-delimited path used for translation lookups
   */
  private static buildFullValuePath(
    parentPath: string,
    childPath: string,
  ): string {
    if (!parentPath) {
      return childPath;
    }
    if (!childPath) {
      return parentPath;
    }
    return `${parentPath}.${childPath}`;
  }

  private static resolveUiLabel(
    value: unknown,
    fallback: string,
    schemaTranslations?: SchemaTranslationsMap,
  ): string {
    const uiLabel = this.readUiLabel(value, fallback);
    return (
      this.getTranslationEntry(uiLabel, schemaTranslations)?.label ?? uiLabel
    );
  }

  /**
   * Resolves the display label for a select-type field option.
   *
   * Fallback chain:
   * 1. `schemaTranslations[leafValuePath]?.values?.[optionValue]`
   * 2. `optionUiLabel` (if provided)
   * 3. `optionValue` itself (last resort)
   *
   * @param optionValue The raw option value from the ui:tree definition
   * @param leafValuePath The valuePath of the parent select field, used to look up the translation entry
   * @param optionUiLabel An optional explicit label declared in the ui:tree option
   * @param schemaTranslations Optional flat translation map keyed by valuePath
   * @returns The resolved human-readable label for the option
   */
  private static resolveSelectOptionLabel(
    optionValue: string,
    leafValuePath: string,
    optionUiLabel: string | undefined,
    schemaTranslations?: SchemaTranslationsMap,
  ): string {
    const translationEntry = this.getTranslationEntry(
      leafValuePath,
      schemaTranslations,
    );
    if (translationEntry?.values?.[optionValue]) {
      return translationEntry.values[optionValue];
    }

    if (optionUiLabel && optionUiLabel.length > 0) {
      return optionUiLabel;
    }

    return optionValue;
  }

  private static getTranslationEntry(
    key: string,
    schemaTranslations?: SchemaTranslationsMap,
  ): SchemaTranslationEntry | undefined {
    return schemaTranslations?.[key];
  }
}
